/** @jsxImportSource @emotion/react */

import { useApolloClient, useMutation, useQuery } from '@apollo/client'
import { IconButton, Switch, Typography } from '@mui/material'
import { useEffect, useMemo, useState } from 'react'
import { ReactComponent as CrossIcon } from 'assets/icons/cross.svg'
import { ReactComponent as EditIcon } from 'assets/icons/edit.svg'
import { bulkUpdateVideoReview, bulkUpdateVideoReviewVariables } from '__generatedTypes__/bulkUpdateVideoReview'
import { findAllPlatforms_findAllPlatforms_items as Platform } from '__generatedTypes__/findAllPlatforms'
import { findOneVideoReview, findOneVideoReviewVariables } from '__generatedTypes__/findOneVideoReview'
import {
  findOneVideoReviewSection,
  findOneVideoReviewSectionVariables,
} from '__generatedTypes__/findOneVideoReviewSection'
import { FileProcessingStatus } from '__generatedTypes__/globalTypes'
import { removeVideoReview, removeVideoReviewVariables } from '__generatedTypes__/removeVideoReview'
import { updateVideoReview, updateVideoReviewVariables } from '__generatedTypes__/updateVideoReview'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import { PageLoader } from 'components/PageLoader'
import Table from 'components/Table'
import TableButtons from 'components/TableButtons/TableButtons'
import { Tooltip } from 'components/Tooltip'
import { PlatformSlug } from 'constants/consts'
import {
  BULK_UPDATE_VIDEO_REVIEW,
  FIND_ONE_VIDEO_REVIEW,
  FIND_ONE_VIDEO_REVIEW_SECTION,
  REMOVE_VIDEO_REVIEW,
  UPDATE_VIDEO_REVIEW,
} from 'queries/videoReview'
import { toast } from 'utils/toast'
import { isRealStudentReviewsAvailable } from '../platformConfig'
import { formatVideoProcessingStatus, hydrateVideoReview, isReviewPublishable, isVideoReviewProcessing } from './api'
import { VIDEO_REVIEW_PROCESSING_POLL_INTERVAL_MS, VIDEO_REVIEW_PROCESSING_POLL_MAX_INTERVAL_MS } from './consts'
import { ReviewModal } from './ReviewModal'
import { ReviewSectionForm } from './ReviewSectionForm'
import { RealStudentReview } from './types'
import * as classes from './styles'

type Props = {
  platform: Platform
}

const getLiveTooltip = (review: RealStudentReview) => {
  if (review.videoProcessingStatus === FileProcessingStatus.FAILED || review.videoProcessingError) {
    return review.videoProcessingError || 'Processing failed'
  }

  if (isVideoReviewProcessing(review.videoProcessingStatus)) {
    return formatVideoProcessingStatus(review.videoProcessingStatus)
  }

  if (!isReviewPublishable(review)) {
    return 'Not publishable — video, thumbnail, and avatar are required'
  }

  return ''
}

export const RealStudentReviews = ({ platform }: Props) => {
  const apolloClient = useApolloClient()
  const platformSlug = platform.slug as PlatformSlug
  const isEnabledPlatform = isRealStudentReviewsAvailable(platformSlug)

  const [reviews, setReviews] = useState<RealStudentReview[]>([])
  const [selectedReview, setSelectedReview] = useState<RealStudentReview | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [deleteReviewId, setDeleteReviewId] = useState<string | null>(null)
  const [submittingReviewIds, setSubmittingReviewIds] = useState(() => new Set<string>())
  const [isReordering, setIsReordering] = useState(false)

  const processingReviewIdsKey = useMemo(
    () =>
      reviews
        .filter((review) => isVideoReviewProcessing(review.videoProcessingStatus))
        .map((review) => review.id)
        .join(','),
    [reviews]
  )

  const {
    data: sectionData,
    loading: isSectionLoading,
    refetch,
  } = useQuery<findOneVideoReviewSection, findOneVideoReviewSectionVariables>(FIND_ONE_VIDEO_REVIEW_SECTION, {
    variables: { platformId: platform.id },
    skip: !isEnabledPlatform,
    fetchPolicy: 'network-only',
    onError: () => toast.error('Failed to load the real student reviews section'),
  })
  const videoReviewSection = sectionData?.findOneVideoReviewSection

  const [updateReview] = useMutation<updateVideoReview, updateVideoReviewVariables>(UPDATE_VIDEO_REVIEW)
  const [bulkUpdateReview] = useMutation<bulkUpdateVideoReview, bulkUpdateVideoReviewVariables>(
    BULK_UPDATE_VIDEO_REVIEW
  )
  const [removeReview, { loading: isDeletingReview }] = useMutation<removeVideoReview, removeVideoReviewVariables>(
    REMOVE_VIDEO_REVIEW
  )

  useEffect(() => {
    if (isReordering) {
      return
    }

    setReviews(
      (videoReviewSection?.videoReviews || [])
        .map((review) => hydrateVideoReview(review, platformSlug))
        .sort((a, b) => a.order - b.order)
    )
  }, [isReordering, platformSlug, videoReviewSection?.videoReviews])

  useEffect(() => {
    if (!processingReviewIdsKey) {
      return
    }

    const reviewIds = processingReviewIdsKey.split(',')
    let isCancelled = false
    let timeoutId: number | undefined
    let consecutiveFailureCount = 0
    let hasReportedPollingError = false

    const pollProcessingReviews = async () => {
      const results = await Promise.allSettled(
        reviewIds.map(async (reviewId) => {
          const { data } = await apolloClient.query<findOneVideoReview, findOneVideoReviewVariables>({
            query: FIND_ONE_VIDEO_REVIEW,
            variables: { id: reviewId },
            fetchPolicy: 'network-only',
          })

          return data.findOneVideoReview
        })
      )

      if (isCancelled) {
        return
      }

      const hasFailures = results.some((result) => result.status === 'rejected')
      const reviews = results.flatMap((result) => (result.status === 'fulfilled' ? [result.value] : []))

      if (hasFailures) {
        consecutiveFailureCount += 1

        if (!hasReportedPollingError) {
          toast.error('Failed to refresh one or more processing review videos. Retrying…')
          hasReportedPollingError = true
        }
      } else {
        consecutiveFailureCount = 0
        hasReportedPollingError = false
      }

      const updatedById = new Map(
        reviews
          .filter((review): review is NonNullable<typeof review> => Boolean(review))
          .map((review) => [review.id, hydrateVideoReview(review, platformSlug)])
      )

      if (updatedById.size) {
        setReviews((items) => items.map((item) => updatedById.get(item.id) || item))
      }

      const nextPollDelay = Math.min(
        VIDEO_REVIEW_PROCESSING_POLL_INTERVAL_MS * 2 ** consecutiveFailureCount,
        VIDEO_REVIEW_PROCESSING_POLL_MAX_INTERVAL_MS
      )

      timeoutId = window.setTimeout(pollProcessingReviews, nextPollDelay)
    }

    timeoutId = window.setTimeout(pollProcessingReviews, VIDEO_REVIEW_PROCESSING_POLL_INTERVAL_MS)

    return () => {
      isCancelled = true

      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId)
      }
    }
  }, [apolloClient, platformSlug, processingReviewIdsKey])

  const handleOpenCreate = () => {
    setSelectedReview(null)
    setIsModalOpen(true)
  }

  const handleOpenEdit = (review: RealStudentReview) => {
    setSelectedReview(review)
    setIsModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    if (!isDeletingReview) {
      setDeleteReviewId(null)
    }
  }

  const handleDelete = async (reviewId: string) => {
    try {
      await removeReview({
        variables: { id: reviewId },
        update: (cache) => {
          cache.evict({
            id: cache.identify({
              __typename: 'VideoReviewType',
              id: reviewId,
            }),
          })
          cache.gc()
        },
      })
    } catch (error) {
      toast.error('Failed to delete review video')
      return
    }

    setReviews((items) => items.filter((item) => item.id !== reviewId))
    setDeleteReviewId(null)
    toast.success('Review video deleted successfully')

    try {
      await refetch()
    } catch (error) {
      // The query's onError handler reports refresh failures without misreporting the successful deletion.
    }
  }

  const handleToggleLive = async (review: RealStudentReview, isLive: boolean) => {
    setSubmittingReviewIds((reviewIds) => new Set(reviewIds).add(review.id))

    try {
      const response = await updateReview({
        variables: {
          updateVideoReviewInputFieldName: {
            id: review.id,
            isLive,
          },
        },
      })
      const updatedReview = response.data?.updateVideoReview

      if (updatedReview) {
        setReviews((items) =>
          items.map((item) => (item.id === updatedReview.id ? hydrateVideoReview(updatedReview, platformSlug) : item))
        )
      }
      toast.success('Review status updated successfully')
    } catch (error) {
      toast.error('Failed to update review status')
    } finally {
      setSubmittingReviewIds((reviewIds) => {
        const nextReviewIds = new Set(reviewIds)

        nextReviewIds.delete(review.id)

        return nextReviewIds
      })
    }
  }

  const writeReviewOrdersToCache = (ordersById: Map<string, number>) => {
    ordersById.forEach((order, id) => {
      const cacheId = apolloClient.cache.identify({ __typename: 'VideoReviewType', id })

      if (!cacheId) {
        return
      }

      apolloClient.cache.modify({
        id: cacheId,
        fields: {
          order: () => order,
        },
      })
    })
  }

  const handleSort = async (sortedReviews: RealStudentReview[]) => {
    if (isReordering) {
      return
    }

    const previousOrderById = new Map(reviews.map(({ id, order }) => [id, order]))
    const nextReviews = sortedReviews.map((review, order) => ({ ...review, order }))
    const nextOrderById = new Map(nextReviews.map(({ id, order }) => [id, order]))

    setIsReordering(true)
    setReviews(nextReviews)
    writeReviewOrdersToCache(nextOrderById)

    try {
      await bulkUpdateReview({
        variables: {
          bulkUpdateVideoReviewInput: nextReviews.map(({ id }, order) => ({ id, order })),
        },
        update: (cache) => {
          nextReviews.forEach(({ id, order }) => {
            const cacheId = cache.identify({ __typename: 'VideoReviewType', id })

            if (!cacheId) {
              return
            }

            cache.modify({
              id: cacheId,
              fields: {
                order: () => order,
              },
            })
          })
        },
      })
      toast.success('Review order updated successfully')
    } catch (error) {
      writeReviewOrdersToCache(previousOrderById)
      setReviews((items) =>
        items
          .map((item) => ({ ...item, order: previousOrderById.get(item.id) ?? item.order }))
          .sort((a, b) => a.order - b.order)
      )
      toast.error('Failed to update review order')
    } finally {
      setIsReordering(false)
    }
  }

  const handleReviewSaved = (review: RealStudentReview) => {
    setReviews((items) => {
      const nextItems = items.some((item) => item.id === review.id)
        ? items.map((item) => (item.id === review.id ? review : item))
        : [...items, review]

      return nextItems.sort((a, b) => a.order - b.order)
    })
    setIsModalOpen(false)

    refetch().catch(() => {
      // The query's onError handler reports refresh failures while preserving the mutation response.
    })
  }

  if (!isEnabledPlatform) {
    return (
      <section css={classes.container}>
        <Typography variant="body2">Real Student Reviews are not enabled for this platform yet.</Typography>
      </section>
    )
  }

  if (isSectionLoading && !videoReviewSection) {
    return <PageLoader />
  }

  if (!videoReviewSection) {
    return (
      <section css={classes.container}>
        <Typography variant="body2">The Real Student Reviews section is unavailable for this platform.</Typography>
      </section>
    )
  }

  const reviewToDelete = reviews.find((review) => review.id === deleteReviewId)
  const currentSelectedReview = selectedReview
    ? reviews.find((review) => review.id === selectedReview.id) || selectedReview
    : null

  return (
    <section css={classes.container}>
      <ReviewSectionForm key={videoReviewSection.id} section={videoReviewSection} />

      <Typography variant="body1" css={classes.tableTitle}>
        Review Videos
      </Typography>

      <Table
        css={classes.table}
        items={reviews}
        isNotLoaded={isSectionLoading}
        isSortable
        onSort={handleSort}
        columns={[
          {
            title: '#',
            flex: '0 1 30px',
            renderCell: (_, index) => <span css={classes.indexCell}>{index + 1}</span>,
          },
          {
            title: 'Reviewer',
            renderCell: (review) => (
              <div css={classes.reviewerCell}>
                {review.thumbnailUrl ? (
                  <img src={review.thumbnailUrl} alt="" css={classes.previewImage} />
                ) : (
                  <div css={classes.previewEmpty} />
                )}
                {review.avatarUrl ? (
                  <img src={review.avatarUrl} alt={review.fullName} css={classes.avatar} />
                ) : (
                  <div css={classes.avatarFallback} />
                )}
                <div css={classes.reviewerText}>
                  <Typography variant="body2">{review.fullName}</Typography>
                  {review.description && (
                    <Typography variant="small1" css={classes.subtleText}>
                      {review.description}
                    </Typography>
                  )}
                </div>
              </div>
            ),
          },
          {
            title: 'Status',
            flex: '0 1 110px',
            renderCell: (review) => {
              const statusLabel = formatVideoProcessingStatus(review.videoProcessingStatus)
              const statusNode = (
                <Typography
                  variant="small1"
                  css={[
                    classes.statusText,
                    review.videoProcessingStatus === FileProcessingStatus.FAILED && classes.statusFailed,
                    isVideoReviewProcessing(review.videoProcessingStatus) && classes.statusProcessing,
                    review.videoProcessingStatus === FileProcessingStatus.COMPLETED && classes.statusCompleted,
                  ]}
                >
                  {statusLabel}
                </Typography>
              )

              if (!review.videoProcessingError) {
                return statusNode
              }

              return (
                <Tooltip title={review.videoProcessingError} isNote arrow>
                  {statusNode}
                </Tooltip>
              )
            },
          },
          {
            title: 'Live',
            flex: '0 1 50px',
            renderCell: (review) => {
              const tooltip = getLiveTooltip(review)
              const isDisabled =
                submittingReviewIds.has(review.id) ||
                isVideoReviewProcessing(review.videoProcessingStatus) ||
                !isReviewPublishable(review)
              const switchNode = (
                <Switch
                  checked={review.isLive}
                  onClick={(event) => event.stopPropagation()}
                  onChange={(_event, checked) => handleToggleLive(review, checked)}
                  disabled={isDisabled}
                  slotProps={{
                    input: {
                      'aria-label': `${review.isLive ? 'Unpublish' : 'Publish'} review by ${review.fullName}`,
                    },
                  }}
                />
              )

              if (!tooltip) {
                return switchNode
              }

              return (
                <Tooltip title={tooltip} isNote arrow>
                  {switchNode}
                </Tooltip>
              )
            },
          },
          {
            flex: '0 1 29px',
            renderCell: (review) => (
              <IconButton
                size="small"
                css={classes.iconButton}
                aria-label={`Edit review by ${review.fullName}`}
                onClick={(event) => {
                  event.stopPropagation()
                  handleOpenEdit(review)
                }}
              >
                <EditIcon />
              </IconButton>
            ),
          },
          {
            flex: '0 1 29px',
            renderCell: (review) => {
              const isProcessing = isVideoReviewProcessing(review.videoProcessingStatus)
              const isDeleteDisabled = isReordering || isProcessing
              const deleteButton = (
                <IconButton
                  size="small"
                  css={[classes.iconButton, isProcessing && classes.iconButtonAriaDisabled]}
                  aria-label={`Delete review by ${review.fullName}`}
                  aria-disabled={isProcessing || undefined}
                  disabled={!isProcessing && isReordering}
                  onClick={(event) => {
                    event.stopPropagation()

                    if (isDeleteDisabled) {
                      return
                    }

                    setDeleteReviewId(review.id)
                  }}
                >
                  <CrossIcon />
                </IconButton>
              )

              if (!isProcessing) {
                return deleteButton
              }

              return (
                <Tooltip title="Wait for video processing to finish before deleting this review." isNote arrow>
                  {deleteButton}
                </Tooltip>
              )
            },
          },
        ]}
      />

      <TableButtons itemName="Review" onAddItem={handleOpenCreate} />

      <ReviewModal
        open={isModalOpen}
        platformSlug={platformSlug}
        videoReviewSectionId={videoReviewSection.id}
        review={selectedReview}
        videoProcessingStatus={currentSelectedReview?.videoProcessingStatus}
        onClose={() => setIsModalOpen(false)}
        onSaved={handleReviewSaved}
      />

      <ConfirmationModal
        open={deleteReviewId !== null}
        title="Deleting review"
        subtitle={`Are you sure you want to delete the review from ${reviewToDelete?.fullName || 'this student'}?`}
        onClose={handleCloseDeleteModal}
        onConfirm={() => deleteReviewId && !isDeletingReview && handleDelete(deleteReviewId)}
        confirmText="Delete"
        closeText="Cancel"
        isLoading={isDeletingReview}
      />
    </section>
  )
}
